/**
 * Shared helpers for proposal rendering (ProposalView, ProposalPreview).
 */

/**
 * Parse an item description into main text and bullet points.
 * Supports bullet markers: -, *, or the bullet character.
 */
export function parseDescription(desc) {
	if (!desc) return { mainText: "", bullets: [] };
	const lines = desc
		.split("\n")
		.map((l) => l.trim())
		.filter(Boolean);
	const bullets = [];
	const mainLines = [];

	for (const line of lines) {
		if (
			line.startsWith("-") ||
			line.startsWith("*") ||
			line.startsWith("\u2022")
		) {
			bullets.push(line.replace(/^[-*\u2022]\s*/, ""));
		} else {
			if (bullets.length === 0) {
				mainLines.push(line);
			} else {
				bullets.push(line);
			}
		}
	}

	if (bullets.length === 0 && mainLines.length > 1) {
		return { mainText: mainLines[0], bullets: mainLines.slice(1) };
	}

	return { mainText: mainLines.join(" "), bullets };
}

/**
 * Format a number as BRL currency string (without the R$ prefix).
 */
export function formatCurrency(value) {
	return parseFloat(value || 0).toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
	});
}

/**
 * Calculate the total value for an array of proposal items.
 */
export function calcTotal(items) {
	return items.reduce(
		(sum, item) => sum + parseFloat(item.value || 0) * (item.quantity || 1),
		0,
	);
}

/**
 * Determine the theme object for a given item based on its theme field or index.
 */
export function getItemTheme(item, index, themes, themeIndexMap) {
	if (item.theme && item.theme !== "auto") {
		return themes[themeIndexMap[item.theme] || 0];
	}
	return themes[index % themes.length];
}

/**
 * Determine the layout string for a given item based on its layout field or index.
 */
export function getItemLayout(item, index) {
	if (item.layout && item.layout !== "auto") return item.layout;
	return index < 3
		? index % 2 === 0
			? "full-width"
			: "featured"
		: "compact-card";
}

/**
 * Compute expiration info from an expiration date.
 * Returns { type: 'expired'|'urgent'|'warning'|'ok', daysLeft: number } or null.
 */
export function getExpirationInfo(expiresAt, isExpired) {
	if (!expiresAt) return null;
	if (isExpired) return { type: "expired", daysLeft: 0 };
	const daysLeft = Math.ceil(
		(new Date(expiresAt) - Date.now()) / (1000 * 60 * 60 * 24),
	);
	if (daysLeft <= 0) return { type: "expired", daysLeft: 0 };
	if (daysLeft < 7) return { type: "urgent", daysLeft };
	if (daysLeft <= 14) return { type: "warning", daysLeft };
	return { type: "ok", daysLeft };
}
/**
 * Helper: parse JSONB field safely
 */
export function parseJsonField(val, fallback = null) {
	if (!val) return fallback;
	try {
		return typeof val === "string" ? JSON.parse(val) : val;
	} catch {
		return fallback;
	}
}

/**
 * Helper: resolve default_items to full item objects
 */
export function resolveDefaultItems(defaultItems, plans = [], modules = []) {
	const di = parseJsonField(defaultItems);
	if (!Array.isArray(di)) return [];
	return di
		.map((item) => {
			let name = "",
				value = 0,
				icon = "",
				subtitle = "",
				image_url = "",
				features = [];
			if (item.item_type === "plan") {
				const plan = plans.find((p) => p.id === item.item_id);
				if (plan) {
					name = plan.name;
					value = plan.value;
					icon = plan.icon || "server";
					subtitle = plan.subtitle || "";
					image_url = plan.image_url || "";
					features = plan.features || [];
				}
			} else {
				const mod = modules.find((m) => m.id === item.item_id);
				if (mod) {
					name = mod.name;
					value = mod.value;
					icon = mod.icon || "box";
					subtitle = mod.subtitle || "";
					image_url = mod.image_url || "";
					features = mod.features || [];
				}
			}
			return {
				...item,
				name,
				value,
				icon,
				subtitle,
				image_url,
				features,
				quantity: item.quantity || 1,
			};
		})
		.filter((i) => i.name);
}

/**
 * Add a new section to an array of sections.
 */
export function addSection(sections, newSection) {
	return [...sections, newSection];
}

/**
 * Remove a section by index.
 */
export function removeSection(sections, index) {
	return sections.filter((_, i) => i !== index);
}

/**
 * Move a section from one index to another.
 */
export function moveSection(sections, fromIdx, toIdx) {
	if (toIdx < 0 || toIdx >= sections.length) return sections;
	const newSections = [...sections];
	const [moved] = newSections.splice(fromIdx, 1);
	newSections.splice(toIdx, 0, moved);
	return newSections;
}

/**
 * Duplicate a section at a given index.
 */
export function duplicateSection(sections, index) {
	const original = sections[index];
	const newSections = [...sections];
	newSections.splice(index + 1, 0, JSON.parse(JSON.stringify(original)));
	return newSections;
}

/**
 * Update a field in a section at a given index.
 */
export function updateSection(sections, index, field, value) {
	const newSections = [...sections];
	newSections[index] = { ...newSections[index], [field]: value };
	return newSections;
}
